"use client";

import { useCallback, useRef, useState } from "react";
import type {
  AwardShowImportActionResult,
  AwardShowImportErrorCode,
  AwardShowImportPreview,
  AwardShowImportRequest,
  AwardShowImportWriteResult,
} from "../award-show-import";

export type AwardShowImportApi = {
  preview: (
    request: AwardShowImportRequest,
  ) => Promise<AwardShowImportActionResult<AwardShowImportPreview>>;
  confirm: (
    request: AwardShowImportRequest & { planHash: string },
  ) => Promise<AwardShowImportActionResult<AwardShowImportWriteResult>>;
};

export type UseAwardShowImportResult = {
  preview: AwardShowImportPreview | null;
  result: AwardShowImportWriteResult | null;
  errorCode: AwardShowImportErrorCode | null;
  isPreviewing: boolean;
  isImporting: boolean;
  runPreview: (request: AwardShowImportRequest) => Promise<void>;
  runImport: () => Promise<AwardShowImportWriteResult | null>;
  reset: () => void;
};

/**
 * Preview, then confirm, an import by show name and year. The confirm call
 * sends the preview's planHash; if the server computes a different plan it
 * returns the fresh preview so the admin can review it again.
 */
export function useAwardShowImport(api: AwardShowImportApi): UseAwardShowImportResult {
  const [preview, setPreview] = useState<AwardShowImportPreview | null>(null);
  const [result, setResult] = useState<AwardShowImportWriteResult | null>(null);
  const [errorCode, setErrorCode] = useState<AwardShowImportErrorCode | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const requestRef = useRef<AwardShowImportRequest | null>(null);

  const reset = useCallback(() => {
    requestRef.current = null;
    setPreview(null);
    setResult(null);
    setErrorCode(null);
  }, []);

  const runPreview = useCallback(
    async (request: AwardShowImportRequest) => {
      requestRef.current = request;
      setIsPreviewing(true);
      setErrorCode(null);
      setPreview(null);
      setResult(null);
      try {
        const response = await api.preview(request);
        if (response.ok) setPreview(response.data);
        else setErrorCode(response.error);
      } catch {
        setErrorCode("unexpected");
      } finally {
        setIsPreviewing(false);
      }
    },
    [api],
  );

  const runImport = useCallback(async () => {
    const request = requestRef.current;
    if (!request || !preview) return null;
    setIsImporting(true);
    setErrorCode(null);
    try {
      const response = await api.confirm({ ...request, planHash: preview.planHash });
      if (response.ok) {
        setResult(response.data);
        return response.data;
      }
      setErrorCode(response.error);
      if (response.preview) setPreview(response.preview);
      return null;
    } catch {
      setErrorCode("unexpected");
      return null;
    } finally {
      setIsImporting(false);
    }
  }, [api, preview]);

  return { preview, result, errorCode, isPreviewing, isImporting, runPreview, runImport, reset };
}
